import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { CalendarClock, CalendarDays, ChevronLeft, ChevronRight, PartyPopper } from "lucide-react";
import { Button, Heading, Text } from "@athena/ui";
import { ClientsDocument } from "@/graphql/clients.generated";
import { MilestoneBadge } from "./components/MilestoneBadge";
import { MilestoneCard } from "./components/MilestoneCard";
import { MilestonesCallout } from "./components/MilestonesCallout";
import { toClientRecord, type ClientRecord } from "./helpers/client";
import {
    addMonths,
    birthdaysIn,
    birthdaysInAWeek,
    birthdaysToday,
    formatMonth,
    formatMonthName,
    MONTH_PARAM,
    parseMonthParam,
    soberAnniversariesIn,
    soberAnniversariesInAWeek,
    soberAnniversariesToday,
    startOfMonth,
    toMonthParam,
    upcoming,
} from "./helpers/milestones";

/**
 * The coordinator's dashboard: for now, a month's upcoming birthdays and sober anniversaries, for
 * planning cards and packages, with today's and next week's called out above. Opens on
 * this month, from today on; the arrows step to the next, which is when a card would actually be
 * ordered. Months already gone are not reachable, since only what is still to come is shown.
 */
export function DashboardPage() {
    const { t } = useTranslation();
    const [
        searchParams,
        setSearchParams,
    ] = useSearchParams();
    const today = useMemo(() => new Date(), []);
    const thisMonth = startOfMonth(today);
    const requested = parseMonthParam(searchParams.get(MONTH_PARAM));
    // A link to a past month opens on this one: there is nothing upcoming in it.
    const month = requested && requested > thisMonth ? requested : thisMonth;
    const { data, loading, error } = useQuery(ClientsDocument);
    const clients = useMemo<ClientRecord[]>(() => (data?.clients ?? []).map(toClientRecord), [data]);

    // Replaced rather than pushed, so Back leaves the page instead of stepping through months.
    const showMonth = (shown: Date) => setSearchParams({ [MONTH_PARAM]: toMonthParam(shown) }, { replace: true });

    const monthName = formatMonthName(month);
    const failed = Boolean(error) && !data;
    const emptyText = (key: "noBirthdays" | "noSoberAnniversaries") =>
        failed ? t("admin.alumni.milestones.loadFailed") : t(`admin.alumni.milestones.${key}`, { month: monthName });

    return (
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10 md:py-8">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="max-w-2xl">
                    <Heading level={2} as="h1">
                        {t("admin.alumni.milestones.title")}
                    </Heading>
                    <Text size="sm" muted className="mt-1">
                        {t("admin.alumni.milestones.description")}
                    </Text>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        aria-label={t("admin.alumni.milestones.previousMonth")}
                        disabled={month <= thisMonth}
                        onClick={() => showMonth(addMonths(month, -1))}
                    >
                        <ChevronLeft />
                    </Button>
                    <Text as="span" weight="semibold" className="min-w-32 text-center" aria-live="polite">
                        {formatMonth(month)}
                    </Text>
                    <Button
                        variant="outline"
                        size="icon"
                        aria-label={t("admin.alumni.milestones.nextMonth")}
                        onClick={() => showMonth(addMonths(month, 1))}
                    >
                        <ChevronRight />
                    </Button>
                    <Button
                        variant="outline"
                        className="w-auto"
                        disabled={month <= thisMonth}
                        onClick={() => showMonth(thisMonth)}
                    >
                        <CalendarDays />
                        {t("admin.alumni.milestones.thisMonth")}
                    </Button>
                </div>
            </header>

            <MilestonesCallout
                title={t("admin.alumni.milestones.todayTitle")}
                icon={<PartyPopper className="h-4 w-4" aria-hidden />}
                when="today"
                birthdays={birthdaysToday(clients, today)}
                anniversaries={soberAnniversariesToday(clients, today)}
            />
            <MilestonesCallout
                title={t("admin.alumni.milestones.inAWeekTitle")}
                icon={<CalendarClock className="h-4 w-4" aria-hidden />}
                when="inAWeek"
                birthdays={birthdaysInAWeek(clients, today)}
                anniversaries={soberAnniversariesInAWeek(clients, today)}
            />

            <div className="grid items-start gap-6 lg:grid-cols-2">
                <MilestoneCard
                    title={t("admin.alumni.milestones.birthdays")}
                    milestones={upcoming(birthdaysIn(clients, month), today)}
                    loading={loading && !data}
                    empty={emptyText("noBirthdays")}
                    highlight={{
                        today,
                        tone: "brand",
                        badge: (_birthday, when) => <MilestoneBadge kind="birthday" when={when} />,
                    }}
                />
                <MilestoneCard
                    title={t("admin.alumni.milestones.soberAnniversaries")}
                    milestones={upcoming(soberAnniversariesIn(clients, month), today)}
                    loading={loading && !data}
                    empty={emptyText("noSoberAnniversaries")}
                    detail={(anniversary) => t("admin.alumni.milestones.years", { count: anniversary.years })}
                    highlight={{
                        today,
                        tone: "success",
                        badge: (anniversary, when) => (
                            <MilestoneBadge kind="soberAnniversary" years={anniversary.years} when={when} />
                        ),
                    }}
                />
            </div>
        </div>
    );
}
